/**
 * Modèles de fichiers d'import, partagés par l'ancien écran et la refonte.
 *
 * Les colonnes sont la seule ligne du fichier généré : son en-tête. Elles vivent ici
 * plutôt que dans un composant parce que deux rendus différents les téléchargent —
 * `Templates` en v1, `ImportTemplatesModal` en v2 — et qu'une divergence entre les deux
 * produirait des fichiers d'import incompatibles.
 */

/**
 * Nature de l'ajout. Volontairement redéfini ici plutôt qu'importé de la refonte :
 * l'ancien écran ne doit pas dépendre du nouveau.
 */
export type TemplateKind = 'maintenance' | 'create';

export type ImportTemplate = {
  /** Clé i18n du nom affiché à l'utilisateur. */
  labelKey: string;
  /** Nom du fichier sans extension : le format choisi la fournit. */
  fileBaseName: string;
  /** En-tête du fichier, dans l'ordre attendu par l'import. */
  columns: string[];
};

/**
 * Les libellés de colonnes étant traduits, la liste se construit à l'appel plutôt que
 * de vivre en constante de module.
 */
export function buildImportTemplates(
  t: (key: string) => string
): Record<TemplateKind, ImportTemplate[]> {
  return {
    maintenance: [
      {
        labelKey: 'bconnectMaintenanceOrder.pdsSelection.templates.maintenanceLink3',
        fileBaseName: 'maintenance_water',
        columns: [
          t('bconnectMaintenanceOrder.pdsSelection.templates.equipment'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.code'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.radioMode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.specificCriterion'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.configuration'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.reasonForIntervention'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.instructions'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.tour'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.datesConstraintType'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.date1'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.date2'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.subcontractor'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.contactName'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.contactPhone')
        ]
      }
    ],
    create: [
      {
        labelKey: 'bconnectMaintenanceOrder.pdsSelection.templates.createLink1',
        fileBaseName: 'pds_water',
        columns: [
          t('bconnectMaintenanceOrder.pdsSelection.templates.code'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.radioMode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.specificCriterion'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.configuration'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.reasonForIntervention'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.instructions'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.tour'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.datesConstraintType'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.date1'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.date2'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.subcontractor'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.contactName'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.contactPhone'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.contract'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.pdsId'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sector'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.subsector'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.level1Group'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.level2Group'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.level3Group'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.streetNumber'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.extensionStreetNumber'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.streetName'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.zipCode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.inseeCode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.city'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.latitude'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.longitude'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorId'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorFamilyId'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorBrand'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorModel'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorWheelsNumber'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorDiameter'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorInstallationDate'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorInstallationIndex'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.deviceId'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.moduleRadioMode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.moduleInstallationDate'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.moduleInstallationIndex'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.moduleMeterKey')
        ]
      },
      {
        labelKey: 'bconnectMaintenanceOrder.pdsSelection.templates.createLink2',
        fileBaseName: 'pds_apilink',
        columns: [
          t('bconnectMaintenanceOrder.pdsSelection.templates.code'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.radioMode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.configuration'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.reasonForIntervention'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.instructions'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.tour'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.datesConstraintType'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.date1'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.date2'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.subcontractor'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.contactName'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.contactPhone'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.contract'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.pdsId'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sector'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.subsector'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.level1Group'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.level2Group'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.level3Group'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.streetNumber'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.extensionStreetNumber'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.streetName'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.zipCode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.inseeCode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.city'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.latitude'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.longitude'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorId'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorFamilyId'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorBrand'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorModel'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorDiameter'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorInstallationDate'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.deviceId'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.moduleRadioMode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.moduleInstallationDate')
        ]
      },
      {
        labelKey: 'bconnectMaintenanceOrder.pdsSelection.templates.createLink3',
        fileBaseName: 'pds_sewerin',
        columns: [
          t('bconnectMaintenanceOrder.pdsSelection.templates.code'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.radioMode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.configuration'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.reasonForIntervention'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.instructions'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.tour'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.datesConstraintType'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.date1'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.date2'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.subcontractor'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.contactName'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.contactPhone'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.contract'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.pdsId'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sector'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.subsector'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.level1Group'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.level2Group'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.level3Group'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.streetNumber'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.extensionStreetNumber'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.streetName'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.zipCode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.inseeCode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.city'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.latitude'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.longitude'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorId'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorFamilyId'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorBrand'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorModel'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorInstallationDate'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.deviceId'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.moduleRadioMode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.moduleInstallationDate')
        ]
      },
      {
        labelKey: 'bconnectMaintenanceOrder.pdsSelection.templates.createLink4',
        fileBaseName: 'pds_pav',
        columns: [
          t('bconnectMaintenanceOrder.pdsSelection.templates.code'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.radioMode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.configuration'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.reasonForIntervention'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.instructions'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.tour'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.datesConstraintType'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.date1'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.date2'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.subcontractor'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.contactName'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.contactPhone'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.contract'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.pdsId'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sector'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.subsector'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.level1Group'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.level2Group'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.level3Group'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.streetNumber'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.extensionStreetNumber'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.streetName'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.zipCode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.inseeCode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.city'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.latitude'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.longitude'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorId'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorFamilyId'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorBrand'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorModel'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorMaterialType'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.devicePosition'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.sensorInstallationDate'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.deviceId'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.moduleRadioMode'),
          t('bconnectMaintenanceOrder.pdsSelection.templates.moduleInstallationDate')
        ]
      }
    ]
  };
}
